
import { debounce } from 'lodash';
import { defineComponent, nextTick, ref } from 'vue';
import { useLoading } from '@/core/hooks/use-loading';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import {
  IDanhMucToaXeResponseBase,
  IDonViResponseBase,
  IKeHoachSuaChuaResponseBase,
} from '@/core/interfaces/ApiResponses';
import { DonViService } from '@/core/services/DonVi.service';
import { KeHoachSuaChuaService } from '@/core/services/KeHoachSuaChua.service';
import { getErrorMsg, swalNotification } from '@/core/helpers/utils';
import { usePagination } from '@/core/hooks/use-pagination';
import { usePageHeader } from '@/core/hooks/usePageHeader';
import useCapDonVi from '@/core/hooks/use-cap-don-vi';
import { KE_HOACH_SUA_CHUA } from '@/core/constants/permissions';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import KeHoachTable from '@/views/crafted/pages/ke-hoach-sua-chua/ke-hoach-table/KeHoachTable.vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageHeaderFilters from '@/layout/header/PageHeaderFilters.vue';
import PageHeaderActions from '@/layout/header/PageHeaderActions.vue';
import PageHeaderActionItem from '@/layout/header/PageHeaderActionItem.vue';
import Pagination from '@/components/shared/pagination/Pagination.vue';
import FilterItem from '@/layout/_shared/FilterItem.vue';
import DanhMucToaXeSearchInput from '@/components/shared/danh-muc-toa-xe-search-input/DanhMucToaXeSearchInput.vue';

export default defineComponent({
  name: 'ke-hoach-sua-chua-list',

  components: {
    DanhMucToaXeSearchInput,
    FilterItem,
    KeHoachTable,
    PageContentBase,
    PageHeaderFilters,
    PageHeaderActions,
    PageHeaderActionItem,
    Pagination,
  },

  setup() {
    const { startLoading, endLoading } = useLoading();
    const { push, goBack } = useRouterCustom();
    const { isCompanyLevel } = useCapDonVi();
    const dmToaXeId = ref(0);
    const selectedDmToaXeLabel = ref('');
    const selectedDonViId = ref<number | ''>('');

    const {
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      loading,
    } = usePagination();

    document.title = 'Kế hoạch sửa chữa - Đường sắt Hà Nội';

    const { searchQuery } = usePageHeader({
      title: 'Danh sách kế hoạch sửa chữa',
      breadcrumbs: [{ text: 'Kế hoạch sửa chữa' }],
      showSearch: true,
      searchPlaceholder: 'Tìm kiếm kế hoạch',
      addLabel: 'Thêm kế hoạch',
      addPermissions: [KE_HOACH_SUA_CHUA.TAO_MOI],
      onAdd: () => push('/quan-ly/ke-hoach-sua-chua/tao-moi'),
    });

    return {
      currentPage,
      dmToaXeId,
      selectedDmToaXeLabel,
      selectedDonViId,
      isCompanyLevel,
      totalItems,
      perPage,
      perPageArr,
      loading,
      searchQuery,
      startLoading,
      endLoading,
      push,
      goBack,
      faPrint,
    };
  },

  data() {
    return {
      donViOptions: [] as IDonViResponseBase[],
      keHoachItems: [] as IKeHoachSuaChuaResponseBase[],
    };
  },

  async mounted() {
    if (this.isCompanyLevel) {
      await this.fetchDonViOptions();
    }

    this.fetchKeHoach();
  },

  watch: {
    async currentPage() {
      await this.fetchKeHoach();
    },

    async perPage() {
      await nextTick();

      if (this.currentPage === 1) {
        await this.fetchKeHoach();
        return;
      }

      this.currentPage = 1;
    },

    async dmToaXeId() {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchKeHoach();
          return;
        }

        this.currentPage = 1;
      });
    },

    async selectedDonViId() {
      this.currentPage = 1;
      await this.fetchKeHoach();
    },

    searchQuery: debounce(function () {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchKeHoach();
          return;
        }

        this.currentPage = 1;
      });
    }, 500),
  },

  methods: {
    openPrintViewer() {
      const selectedDonVi = this.donViOptions.find(
        (item) => item.id === this.selectedDonViId
      );

      const route = this.$router.resolve({
        name: 'xem-danh-sach-ke-hoach-sua-chua',
        query: {
          ...(this.dmToaXeId
            ? {
                idDmToaXe: String(this.dmToaXeId),
                tenDm: this.selectedDmToaXeLabel,
              }
            : {}),
          ...(this.selectedDonViId
            ? {
                idDonVi: String(this.selectedDonViId),
                tenDonVi: selectedDonVi?.ten_don_vi || '',
              }
            : {}),
          ...(this.searchQuery ? { s: this.searchQuery } : {}),
        },
      });

      window.open(route.href, '_blank');
    },

    handleDmClear() {
      this.dmToaXeId = 0;
      this.selectedDmToaXeLabel = '';
    },

    handleDmSelect({ id, ten_danh_muc }: IDanhMucToaXeResponseBase) {
      this.dmToaXeId = id;
      this.selectedDmToaXeLabel = ten_danh_muc;
    },

    async fetchDonViOptions() {
      try {
        const {
          data: { data },
        } = await DonViService.fetchDepartmentsByCapDonVi(2);

        this.donViOptions = data;
      } catch (err) {
        await swalNotification(
          getErrorMsg(err, 'Có lỗi khi tải danh sách đơn vị'),
          'error',
        );
      }
    },

    async fetchKeHoach() {
      try {
        this.startLoading();

        const {
          data: {
            data: { data, total },
          },
        } = await KeHoachSuaChuaService.list({
          per_page: this.perPage,
          current_page: this.currentPage,
          s: this.searchQuery,
          id_dm_toa_xe: this.dmToaXeId,
          ...(this.selectedDonViId
            ? { id_don_vi: this.selectedDonViId }
            : {}),
        });

        this.keHoachItems = data;
        this.totalItems = total;
        this.endLoading();
      } catch (err) {
        this.endLoading();

        await swalNotification(
          getErrorMsg(
            err,
            'Có lỗi xảy ra khi lấy thông tin kế hoạch sửa chữa',
          ),
          'error',
        );
      }
    },
  },
});
